import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

import { pxToRem, withAlpha } from "../../styles/utils";

export type StudentStatusTone = "neutral" | "info" | "warning" | "success";

export const CourseLessonPageShell = styled.div`
  width: 100%;
  padding-bottom: ${({ theme }) => pxToRem(theme.spacing.xxl)};

  @media (max-width: 720px) {
    padding-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  }
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.radii.sm)};
  padding: 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(3)};
  }
`;

export const BackChevron = styled.span`
  width: ${pxToRem(9)};
  height: ${pxToRem(9)};
  flex: 0 0 auto;
  border-bottom: ${pxToRem(1.5)} solid currentColor;
  border-left: ${pxToRem(1.5)} solid currentColor;
  transform: rotate(45deg);
`;

export const EntityTitle = styled.h1`
  margin: ${({ theme }) => pxToRem(theme.spacing.sm)} 0 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const EntityMeta = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.radii.sm)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const EntityDescription = styled.p`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.6;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

export const SectionHeading = styled.h2`
  margin: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.4;
`;

export const CourseSummary = styled.section`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} ${({ theme }) => pxToRem(theme.spacing.md)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const SummaryBlock = styled.div<{ $grow?: boolean }>`
  display: flex;
  min-width: 0;
  flex: ${({ $grow }) => ($grow ? `1 1 ${pxToRem(240)}` : "0 1 auto")};
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.radii.sm)};
`;

export const SummaryLabel = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const SummaryTrack = styled.span`
  width: 100%;
  height: ${pxToRem(6)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.inkSurface};
`;

export const SummaryProgress = styled.span<{ $percent: number }>`
  display: block;
  width: ${({ $percent }) => `${Math.max(0, Math.min(100, $percent))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand};
`;

export const SummaryValue = styled.span`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
  }
`;

export const SummaryDivider = styled.span`
  width: ${pxToRem(1)};
  align-self: stretch;
  background: ${({ theme }) => theme.borders.divider};

  @media (max-width: 720px) {
    display: none;
  }
`;

const statusTone = css<{ $tone?: StudentStatusTone }>`
  ${({ $tone, theme }) => {
    if ($tone === "success") {
      return css`
        background: ${withAlpha(theme.colors.success, 0.14)};
        color: ${theme.colors.successText};
      `;
    }
    if ($tone === "warning") {
      return css`
        background: ${withAlpha(theme.colors.warningMuted, 0.14)};
        color: ${theme.colors.warningMutedText};
      `;
    }
    if ($tone === "info") {
      return css`
        background: ${theme.colors.brandSoft};
        color: ${theme.colors.brand};
      `;
    }
    return css`
      background: ${theme.colors.inkSurface};
      color: ${theme.colors.inkSoft};
    `;
  }}
`;

export const DemoStatusChip = styled.span<{ $tone?: StudentStatusTone }>`
  ${statusTone};
  display: inline-flex;
  width: fit-content;
  min-height: ${pxToRem(25)};
  align-items: center;
  padding: ${({ theme }) => pxToRem(theme.spacing.xxs)} ${({ theme }) => pxToRem(theme.spacing.xs)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`;

export const LessonsList = styled.div`
  overflow: hidden;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const LessonRow = styled(Link)`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} ${({ theme }) => pxToRem(theme.spacing.md)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  color: inherit;
  text-align: left;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceFaint};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(-2)};
  }
`;

export const LessonOrder = styled.span`
  display: inline-flex;
  width: ${pxToRem(30)};
  height: ${pxToRem(30)};
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.inkSurface};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
`;

export const LessonMain = styled.span`
  min-width: 0;
  flex: 1 1 auto;
`;

export const LessonTitle = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.4;
`;

export const LessonMarks = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)} ${({ theme }) => pxToRem(theme.spacing.xs)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const LessonMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const LessonMarkIcon = styled.span`
  display: inline-flex;
  width: ${pxToRem(12)};
  height: ${pxToRem(14)};
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.brand};

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const LessonChevron = styled.span`
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  margin-right: ${pxToRem(2)};
  flex: 0 0 auto;
  border-top: ${pxToRem(1.6)} solid ${({ theme }) => theme.colors.inkMuted};
  border-right: ${pxToRem(1.6)} solid ${({ theme }) => theme.colors.inkMuted};
  transform: rotate(45deg);
`;

export const LessonContentSection = styled.section`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const RichLessonText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.65;
  overflow-wrap: anywhere;
  white-space: pre-wrap;

  p {
    margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    padding-left: ${({ theme }) => pxToRem(theme.spacing.lg)};
  }

  li {
    margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  }
`;

export const MediaItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

const mediaRow = css`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: inherit;
  text-decoration: none;
`;

export const MediaRowLink = styled.a`
  ${mediaRow};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.surfaceFaint};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const MediaRowStatic = styled.div`
  ${mediaRow};
`;

export const MediaIcon = styled.span`
  display: inline-flex;
  width: ${pxToRem(14)};
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.brand};

  svg {
    width: 100%;
    height: ${pxToRem(18)};
  }
`;

export const MediaMain = styled.span`
  min-width: 0;
  flex: 1 1 auto;
`;

export const MediaName = styled.span`
  display: block;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MediaSub = styled.span`
  display: block;
  margin-top: ${pxToRem(2)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const MediaProgress = styled.span`
  display: block;
  height: ${pxToRem(3)};
  overflow: hidden;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.inkSurface};
`;

export const MediaProgressFill = styled.span<{ $percent: number }>`
  display: block;
  width: ${({ $percent }) => `${Math.max(0, Math.min(100, $percent))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand};
`;

export const MediaAction = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.3;
`;

export const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const AssignmentRow = styled(Link)`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} ${({ theme }) => pxToRem(theme.spacing.md)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brandMedium};
    background: ${({ theme }) => theme.colors.surfaceFaint};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const AssignmentMain = styled.span`
  min-width: 0;
  flex: 1 1 auto;
`;

export const AssignmentTitle = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.4;
`;

export const AssignmentDescription = styled.span`
  display: -webkit-box;
  overflow: hidden;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

export const DemoEmptyState = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
  text-align: center;
`;
